import { useEffect, useRef } from 'react';

import './ScrollFloat.css';

interface ScrollFloatProps {
  children: React.ReactNode;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  containerClassName?: string;
  textClassName?: string;
  animationDuration?: number;
  ease?: string;
  scrollStart?: string;
  scrollEnd?: string;
  stagger?: number;
}

/**
 * Lightweight CSS + IntersectionObserver replacement for the previous
 * GSAP/ScrollTrigger implementation. Same public API, but no per-character
 * DOM split, no forced reflows, no GSAP dependency — animation runs on
 * transform/opacity only (compositor-friendly).
 */
const ScrollFloat = ({
  children,
  containerClassName = '',
  textClassName = '',
  animationDuration = 0.8,
}: ScrollFloatProps) => {
  const containerRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Honor reduced-motion preference.
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      el.classList.add('scroll-float--visible');
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('scroll-float--visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <h2
      ref={containerRef}
      className={`scroll-float ${containerClassName}`}
      style={{ ['--scroll-float-duration' as string]: `${animationDuration}s` }}
    >
      <span className={`scroll-float-text ${textClassName}`}>{children}</span>
    </h2>
  );
};

export default ScrollFloat;
